import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Bell, Rocket } from "lucide-react";
import type { LandingStats } from "@/lib/landingStats";
import { defaultLandingStats } from "@/lib/landingStats";

interface Props {
  theme: "dark" | "light";
  lang: "fr" | "en" | "ar";
  stats: LandingStats;
}

const content = {
  fr: {
    badge: "🚀 Disponible maintenant",
    h2: "Téléchargez Mawthoq",
    sub: "Gratuit. Disponible sur iOS et Android.",
    appStore: "Télécharger sur l'App Store",
    googlePlay: "Disponible sur Google Play",
    social: "★★★★★  Rejoignez des milliers de conducteurs mauritaniens",
  },
  en: {
    badge: "🚀 Available now",
    h2: "Download Mawthoq",
    sub: "Free. Available on iOS and Android.",
    appStore: "Download on the App Store",
    googlePlay: "Get it on Google Play",
    social: "★★★★★  Join thousands of Mauritanian drivers",
  },
  ar: {
    badge: "🚀 متاح الآن",
    h2: "حمّل موثوق",
    sub: "مجاني. متاح على iOS وAndroid.",
    appStore: "تحميل من App Store",
    googlePlay: "احصل عليه من Google Play",
    social: "★★★★★  انضم إلى آلاف السائقين الموريتانيين",
  },
} as const;

const comingSoonCopy: Record<
  Props["lang"],
  { title: string; sub: string; cta: string; labels: [string, string, string] }
> = {
  fr: {
    title: "Bientôt disponible",
    sub: "Notre application sera bientôt disponible sur le store. Restez à l'écoute !",
    cta: "C'est compris !",
    labels: ["Nouveau", "Alertes", "Rapide"],
  },
  en: {
    title: "Coming Soon",
    sub: "Our app is coming to the store soon. Stay tuned!",
    cta: "Got it!",
    labels: ["New", "Alerts", "Fast"],
  },
  ar: {
    title: "قريباً",
    sub: "تطبيقنا سيكون متاحاً على المتجر قريباً. ترقّب!",
    cta: "حسناً!",
    labels: ["جديد", "تنبيهات", "سريع"],
  },
};

interface ComingSoonPopupProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Props["lang"];
}

function ComingSoonPopup({ isOpen, onClose, lang }: ComingSoonPopupProps) {
  const [particles, setParticles] = useState<
    Array<{ id: number; x: number; y: number; delay: number }>
  >([]);
  const copy = comingSoonCopy[lang];

  useEffect(() => {
    if (isOpen) {
      setParticles(
        Array.from({ length: 20 }, (_, i) => ({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 2,
        }))
      );
    }
  }, [isOpen]);

  const featureIcons = [Sparkles, Bell, Rocket] as const;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-md"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="relative w-full max-w-md pointer-events-auto"
              dir={lang === "ar" ? "rtl" : "ltr"}
            >
              {/* Glow */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 opacity-75 blur-xl animate-pulse" />

              {/* Card */}
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95 p-8 shadow-2xl">
                {/* Floating particles */}
                {particles.map((p) => (
                  <motion.div
                    key={p.id}
                    className="absolute h-1 w-1 rounded-full bg-cyan-400/60"
                    style={{ left: `${p.x}%`, top: `${p.y}%` }}
                    animate={{ y: [-20, 20, -20], x: [-10, 10, -10], opacity: [0.3, 0.8, 0.3], scale: [0.8, 1.2, 0.8] }}
                    transition={{ duration: 3, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
                  />
                ))}

                {/* Close button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="absolute right-4 top-4 rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </motion.button>

                {/* Rocket icon */}
                <motion.div
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                >
                  <div className="relative">
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-cyan-400/30"
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-400/30"
                      animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, delay: 0.5, repeat: Infinity }}
                    />
                    <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30">
                      <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <Rocket className="h-10 w-10 text-white" />
                      </motion.div>
                    </div>
                  </div>
                </motion.div>

                {/* Text content */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center"
                >
                  <h3 className="mb-2 bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-2xl font-bold text-transparent">
                    {copy.title}
                  </h3>
                  <p className="mb-6 text-gray-400">{copy.sub}</p>

                  {/* Feature icons */}
                  <div className="mb-6 flex justify-center gap-6">
                    {featureIcons.map((Icon, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + i * 0.1 }}
                        className="flex flex-col items-center gap-1"
                      >
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 15 }}
                          className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-cyan-400"
                        >
                          <Icon className="h-5 w-5" />
                        </motion.div>
                        <span className="text-xs text-gray-500">{copy.labels[i]}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* CTA button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 p-px font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40"
                >
                  <span className="relative flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-4">
                    <motion.span
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      initial={{ x: "-100%" }}
                      animate={{ x: "200%" }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    />
                    <span className="relative text-lg">{copy.cta}</span>
                  </span>
                </motion.button>

                {/* Bottom decoration */}
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                  <span>iOS &amp; Android</span>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function DownloadSection({ theme, lang, stats }: Props) {
  const s = stats ?? defaultLandingStats;
  const isDark = theme === "dark";
  const isRtl = lang === "ar";
  const t = content[lang];
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";
  const cardBg = isDark ? "bg-[rgba(28,28,30,0.85)] border-white/10" : "bg-white/85 border-black/10";

  const fmt = (n: number) => n.toLocaleString(lang === "ar" ? "ar-MA" : lang === "fr" ? "fr-MA" : "en-US");

  const hasAppStoreUrl = Boolean(s.appStoreUrl?.trim());
  const hasPlayStoreUrl = Boolean(s.playStoreUrl?.trim());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <ComingSoonPopup isOpen={comingSoonOpen} onClose={() => setComingSoonOpen(false)} lang={lang} />
      <section
      id="download"
      className="py-28 relative overflow-hidden"
      style={{ background: isDark ? "#050508" : "#EAEAF0" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(0,122,255,0.14) 0%, transparent 60%)",
        }}
      />
      <div
        ref={sectionRef}
        className="max-w-[800px] mx-auto px-6 md:px-10 text-center"
        style={{
          opacity: 0,
          transform: "translateY(30px)",
          transition: "opacity 0.7s ease, transform 0.7s ease",
        }}
      >
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-semibold"
          style={{
            background: "rgba(0,122,255,0.12)",
            border: "1px solid rgba(0,122,255,0.25)",
            color: "#007AFF",
          }}
        >
          {t.badge}
        </div>

        <h2
          className={`text-4xl md:text-6xl font-black tracking-tight mb-4 leading-tight ${textPrimary}`}
          style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}
        >
          {t.h2}
        </h2>
        <p className={`text-lg mb-10 ${textMuted}`}>{t.sub}</p>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
          {hasAppStoreUrl ? (
            <a
              href={s.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] ${
                isDark ? "bg-black border border-white/15 text-white" : "bg-gray-900 text-white"
              }`}
              style={{ backdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.35)" }}
            >
              <i className="ri-apple-fill text-2xl" />
              <div className="flex flex-col items-start">
                <span className="text-[11px] opacity-60 leading-none">
                  {lang === "fr" ? "Télécharger sur" : lang === "en" ? "Download on the" : "تحميل من"}
                </span>
                <span className="text-base font-bold leading-tight">App Store</span>
              </div>
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setComingSoonOpen(true)}
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] ${
                isDark ? "bg-black border border-white/15 text-white" : "bg-gray-900 text-white"
              }`}
              style={{ backdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.35)" }}
            >
              <i className="ri-apple-fill text-2xl" />
              <div className="flex flex-col items-start">
                <span className="text-[11px] opacity-60 leading-none">
                  {lang === "fr" ? "Télécharger sur" : lang === "en" ? "Download on the" : "تحميل من"}
                </span>
                <span className="text-base font-bold leading-tight">App Store</span>
              </div>
            </button>
          )}
          {hasPlayStoreUrl ? (
            <a
              href={s.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-4 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] bg-[#007AFF] text-white"
              style={{ boxShadow: "0 8px 32px rgba(0,122,255,0.40)" }}
            >
              <i className="ri-google-play-fill text-2xl" />
              <div className="flex flex-col items-start">
                <span className="text-[11px] opacity-80 leading-none">
                  {lang === "fr" ? "Disponible sur" : lang === "en" ? "Get it on" : "احصل عليه من"}
                </span>
                <span className="text-base font-bold leading-tight">Google Play</span>
              </div>
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setComingSoonOpen(true)}
              className="flex items-center gap-3 px-6 py-4 rounded-2xl cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] bg-[#007AFF] text-white"
              style={{ boxShadow: "0 8px 32px rgba(0,122,255,0.40)" }}
            >
              <i className="ri-google-play-fill text-2xl" />
              <div className="flex flex-col items-start">
                <span className="text-[11px] opacity-80 leading-none">
                  {lang === "fr" ? "Disponible sur" : lang === "en" ? "Get it on" : "احصل عليه من"}
                </span>
                <span className="text-base font-bold leading-tight">Google Play</span>
              </div>
            </button>
          )}
        </div>

        <p className={`text-sm ${textMuted}`}>{t.social}</p>

        <div className="mt-12 grid grid-cols-3 gap-4">
          {[
            {
              icon: "ri-star-fill",
              color: "#FF9500",
              val: `${s.rating.toFixed(1)}/5`,
              label:
                lang === "fr" ? "Note moyenne" : lang === "en" ? "Average rating" : "متوسط التقييم",
            },
            {
              icon: "ri-download-line",
              color: "#34C759",
              val: `${fmt(s.downloads)}+`,
              label: lang === "fr" ? "Téléchargements" : lang === "en" ? "Downloads" : "تحميل",
            },
            {
              icon: "ri-shield-check-line",
              color: "#007AFF",
              val: "100%",
              label: lang === "fr" ? "Gratuit" : lang === "en" ? "Free" : "مجاني",
            },
          ].map((row, i) => (
            <div key={i} className={`p-5 rounded-2xl backdrop-blur-[20px] border ${cardBg}`}>
              <i className={`${row.icon} text-2xl mb-2 block`} style={{ color: row.color }} />
              <div
                className={`text-xl font-bold ${textPrimary}`}
                style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}
              >
                {row.val}
              </div>
              <div className={`text-xs mt-1 ${textMuted}`}>{row.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
}
